import { useState, useEffect } from "react";
import { FiCheck, FiX, FiChevronLeft, FiChevronRight, FiCalendar } from "react-icons/fi";
import moment from "moment-timezone";
import {
  getAvailability,
  AvailabilityResponse
} from "../../../../actions/stay-actions";

// Eastern Time zone for Florida properties
const TIMEZONE = "America/New_York";

interface StayExtensionCalendarProps {
  checkOut: string;
  onSelectDate: (date: Date, nights: number) => void;
  hasPendingRequest: boolean;
}

function StayExtensionCalendar({ checkOut, onSelectDate, hasPendingRequest }: StayExtensionCalendarProps) {
  const [availability, setAvailability] = useState<AvailabilityResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  const checkOutDate = new Date(checkOut);

  // Format date in Eastern Time
  const formatShortDate = (dateString: string | null) => {
    if (!dateString) return "N/A";
    return moment.utc(dateString).tz(TIMEZONE).format("MMM D");
  };

  // Set initial month to check-out date's month
  useEffect(() => {
    setCurrentMonth(new Date(checkOut));
  }, [checkOut]);

  // Fetch extension availability
  useEffect(() => {
    const fetchAvailability = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await getAvailability(checkOut);
        setAvailability(response.data);
      } catch (err: any) {
        console.error("Failed to fetch extension availability:", err);
        setError("Unable to check availability");
      } finally {
        setLoading(false);
      }
    };

    fetchAvailability();
  }, [checkOut]);

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    return { daysInMonth, startingDayOfWeek, year, month };
  };

  const isSameDay = (date1: Date, date2: Date) => {
    return date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate();
  };

  const getToday = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  };

  const isToday = (date: Date) => {
    return isSameDay(date, getToday());
  };

  const getDayStatus = (date: Date) => {
    const today = getToday();

    // Checkout day (current departure)
    if (isSameDay(date, checkOutDate)) {
      return "checkout";
    }

    // Days before checkout are part of current stay
    if (date < checkOutDate) {
      return "current-stay";
    }

    // Days in the past (before today)
    if (date < today) {
      return "past";
    }

    // Available for extension (after checkout, within availability window)
    if (availability && date > checkOutDate) {
      const availableUntil = availability.availableUntil
        ? new Date(availability.availableUntil)
        : new Date(checkOutDate.getTime() + 30 * 24 * 60 * 60 * 1000); // Default 30 days

      if (date <= availableUntil) {
        return "available";
      } else {
        return "unavailable";
      }
    }

    return "unavailable";
  };

  const navigateMonth = (direction: "prev" | "next") => {
    setCurrentMonth(prev => {
      const newDate = new Date(prev);
      if (direction === "prev") {
        newDate.setMonth(newDate.getMonth() - 1);
      } else {
        newDate.setMonth(newDate.getMonth() + 1);
      }
      return newDate;
    });
  };

  const handleDateClick = (date: Date, status: string) => {
    if (status === "available" && !hasPendingRequest) {
      setSelectedDate(date);
      // Calculate nights and notify parent immediately
      const nights = Math.ceil((date.getTime() - checkOutDate.getTime()) / (1000 * 60 * 60 * 24));
      onSelectDate(date, nights);
    }
  };

  const { daysInMonth, startingDayOfWeek, year, month } = getDaysInMonth(currentMonth);
  const weekDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const monthName = currentMonth.toLocaleDateString("en-US", { month: "long", year: "numeric" });

  // Generate calendar grid
  const calendarGrid: (number | null)[] = [];
  for (let i = 0; i < startingDayOfWeek; i++) {
    calendarGrid.push(null);
  }
  for (let day = 1; day <= daysInMonth; day++) {
    calendarGrid.push(day);
  }

  if (loading) {
    return (
      <div className="calendar-loading">
        <div className="loading-spinner" />
        <p>Checking availability...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="calendar-error">
        <FiX size={24} />
        <p>{error}</p>
      </div>
    );
  }

  if (!availability) {
    return (
      <div className="calendar-empty">
        <FiCalendar size={24} />
        <p>Unable to load availability</p>
      </div>
    );
  }

  return (
    <div className="extension-calendar">
      {/* Availability Status */}
      <div className={`calendar-status ${availability.availableDays > 0 ? 'calendar-status--available' : 'calendar-status--unavailable'}`}>
        {availability.availableDays > 0 ? (
          <FiCheck size={16} />
        ) : (
          <FiX size={16} />
        )}
        <span>
          {availability.availableDays > 0
            ? `${availability.availableDays} day${availability.availableDays > 1 ? 's' : ''} available${availability.hasNextReservation ? ` until ${formatShortDate(availability.availableUntil)}` : ''}`
            : "No availability for extension"}
        </span>
      </div>

      {/* Calendar */}
      <div className="calendar-grid-container">
        {/* Month Navigation */}
        <div className="calendar-nav">
          <button
            className="calendar-nav__btn"
            onClick={() => navigateMonth("prev")}
          >
            <FiChevronLeft size={18} />
          </button>
          <span className="calendar-nav__month">{monthName}</span>
          <button
            className="calendar-nav__btn"
            onClick={() => navigateMonth("next")}
          >
            <FiChevronRight size={18} />
          </button>
        </div>

        {/* Week Day Headers */}
        <div className="calendar-weekdays">
          {weekDays.map(day => (
            <div key={day} className="calendar-weekday">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Grid */}
        <div className="calendar-days">
          {calendarGrid.map((day, index) => {
            if (day === null) {
              return <div key={`empty-${index}`} className="calendar-day calendar-day--empty" />;
            }

            const date = new Date(year, month, day);
            const status = getDayStatus(date);
            const isTodayDate = isToday(date);
            const isSelected = selectedDate && isSameDay(date, selectedDate);
            const isClickable = status === "available" && !hasPendingRequest;

            return (
              <div
                key={day}
                className={`calendar-day calendar-day--${status} ${isSelected ? 'calendar-day--selected' : ''} ${isTodayDate ? 'calendar-day--today' : ''} ${isClickable ? 'calendar-day--clickable' : ''}`}
                onClick={() => handleDateClick(date, status)}
              >
                {day}
              </div>
            );
          })}
        </div>

        {/* Legend */}
        <div className="calendar-legend">
          <div className="calendar-legend__item">
            <div className="calendar-legend__color calendar-legend__color--stay" />
            <span>Stay</span>
          </div>
          <div className="calendar-legend__item">
            <div className="calendar-legend__color calendar-legend__color--checkout" />
            <span>Checkout</span>
          </div>
          <div className="calendar-legend__item">
            <div className="calendar-legend__color calendar-legend__color--available" />
            <span>Available</span>
          </div>
          <div className="calendar-legend__item">
            <div className="calendar-legend__color calendar-legend__color--unavailable" />
            <span>Booked</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default StayExtensionCalendar;
